import type { PurchaseUpsellResponse } from "@features/shared/entities/subscription";

// Solidgate 1-click upsell charges are merchant-initiated against the stored
// recurring token, so there is no payment form where the issuer's 3D Secure
// challenge could render. When the issuer demands one, the order comes back
// in status `3ds_verify` together with `verify_url` (the ACS page). Solidgate:
// "The merchant must display the bank's ACS URL to customers"
// (https://docs.solidgate.com/payments/card-payments/manage-card-payments/).
// Without showing it the order expires into `auth_failed`.
export const UPSELL_3DS_VERIFY_STATUS = "3ds_verify";

// Order statuses meaning "Solidgate has not decided yet" — the normal answer to
// a card 1-click charge. The verdict (auth_ok, a decline, or `3ds_verify` +
// `verify_url`) follows 2–4s later. Upsells are an impulse purchase, so the
// funnel moves on immediately as it always did; the order status is polled in
// the background and a late `3ds_verify` opens the bank dialog wherever the
// user is by then (next funnel screen, /home after the last one, a pack page).
// Without that the challenge was never shown and the order expired as
// Solidgate error 2.10 "3DS URL was not displayed".
export const UPSELL_CHARGE_PENDING_STATUSES = ["processing", "created"];
export const UPSELL_CHARGE_VERDICT_POLL_INTERVAL_MS = 2000;
// Interval ticks before the background poll gives up: polls at 2..12s, stops at
// 14s. Production verdicts land within 4s for 99% of orders.
export const UPSELL_CHARGE_VERDICT_POLL_MAX_TICKS = 7;

export const isUpsellChargeVerdictPending = (response: PurchaseUpsellResponse): boolean =>
    typeof response.status === "string" && UPSELL_CHARGE_PENDING_STATUSES.includes(response.status);

// Route Solidgate redirects the challenge iframe to after authentication
// (`success_url` / `fail_url` of the charge request). The page there posts the
// outcome back to the opener window via postMessage.
export const UPSELL_3DS_RESULT_PATH = "/upsell-3ds-result";
export const UPSELL_3DS_MESSAGE_SOURCE = "tixu-upsell-3ds";

// Student metadata key holding the Solidgate order ids that went through a 3DS
// challenge. An abandoned challenge ends as `auth_failed`, which is not a card
// decline and must not count towards the upsell fail limit — also after a
// reload or in another tab, hence persisted next to the failed-attempt ids.
export const UPSELL_3DS_ORDER_IDS_KEY = "upsell_3ds_order_ids";
export const UPSELL_3DS_ORDER_IDS_LIMIT = 50;

export const parseUpsell3dsOrderIds = (raw: string | undefined): string[] => {
    if (!raw) return [];
    try {
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
    } catch {
        return [];
    }
};

export const mergeUpsell3dsOrderIds = (current: string[], incoming: string[]): string[] => {
    const merged = [...current];
    for (const id of incoming) if (!merged.includes(id)) merged.push(id);
    return merged.slice(-UPSELL_3DS_ORDER_IDS_LIMIT);
};

export type Upsell3dsOutcome = "success" | "fail";
// `closed`: the user dismissed the challenge before finishing it.
export type Upsell3dsResult = Upsell3dsOutcome | "closed";

export type Upsell3dsChallenge = {
    orderId: string;
    verifyUrl: string;
    // Upsell whose purchase is being verified; used to poll its status while
    // the challenge is open. Null when the charge started outside an upsell screen.
    upsellId: string | null;
    // Original charge response, re-emitted as the success signal once verified.
    response: PurchaseUpsellResponse;
    // The challenge surfaced from the background poll after the funnel had
    // already moved on: finishing it must not navigate again or open the
    // failed-purchase modal, the user is on another screen by now.
    late: boolean;
};

export type Upsell3dsResultMessage = {
    source: typeof UPSELL_3DS_MESSAGE_SOURCE;
    status: Upsell3dsOutcome;
};

export const isUpsell3dsChallenge = (
    response: PurchaseUpsellResponse
): response is PurchaseUpsellResponse & { verify_url: string } =>
    response.status === UPSELL_3DS_VERIFY_STATUS &&
    typeof response.verify_url === "string" &&
    response.verify_url.length > 0;

export const buildUpsell3dsReturnUrls = (origin: string) => {
    const returnUrl = `${origin}${UPSELL_3DS_RESULT_PATH}`;
    return {
        successUrl: `${returnUrl}?status=success`,
        failUrl: `${returnUrl}?status=fail`
    };
};

export const parseUpsell3dsOutcome = (value: string | null | undefined): Upsell3dsOutcome | null => {
    if (value === "success" || value === "fail") return value;
    return null;
};

export const buildUpsell3dsResultMessage = (status: Upsell3dsOutcome): Upsell3dsResultMessage => ({
    source: UPSELL_3DS_MESSAGE_SOURCE,
    status
});

// Validates a `message` event payload from the challenge iframe. Origin is
// checked by the caller (the result page is served from our own origin).
export const parseUpsell3dsResultMessage = (data: unknown): Upsell3dsOutcome | null => {
    if (typeof data !== "object" || data === null) return null;
    const message = data as Partial<Upsell3dsResultMessage>;
    if (message.source !== UPSELL_3DS_MESSAGE_SOURCE) return null;
    return parseUpsell3dsOutcome(message.status);
};
